import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Player } from "@gumlet/player.js";
import { Play, Check, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  formatRuntime,
  gumletEmbedUrl,
  totalRuntimeLabel,
  type DemoTopic,
  type Importance,
} from "@/lib/demo-topics";

interface PlaylistItem {
  id: string;
  title: string;
  videoId: string;
  seconds: number | null;
  version: "long" | "short" | "opening";
}

const choices: { value: Importance; label: string }[] = [
  { value: "important", label: "Important" },
  { value: "somewhat", label: "Somewhat" },
  { value: "not", label: "Not important" },
];

/* ------------------------------------------------------------- component */

export function InstantDemo() {
  const [topics, setTopics] = useState<DemoTopic[] | null>(null);
  const [selections, setSelections] = useState<Record<string, Importance>>({});
  const [playlist, setPlaylist] = useState<PlaylistItem[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [soundConfirmed, setSoundConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("demo_topics")
      .select(
        "id,title,blurb,long_video_id,long_seconds,short_video_id,short_seconds,sort_order,is_opening,published",
      )
      .eq("published", true)
      .order("is_opening", { ascending: false })
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (!cancelled) setTopics((data as DemoTopic[] | null) ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const opening = useMemo(() => topics?.find((t) => t.is_opening) ?? null, [topics]);
  const choiceTopics = useMemo(() => topics?.filter((t) => !t.is_opening) ?? [], [topics]);
  const answeredCount = choiceTopics.filter((t) => selections[t.id]).length;

  const preview = useMemo(() => {
    const items: PlaylistItem[] = [];
    if (opening?.long_video_id) {
      items.push({
        id: opening.id,
        title: opening.title,
        videoId: opening.long_video_id,
        seconds: opening.long_seconds,
        version: "opening",
      });
    }
    for (const topic of choiceTopics) {
      const level = selections[topic.id];
      if (level === "important" && topic.long_video_id) {
        items.push({
          id: topic.id,
          title: topic.title,
          videoId: topic.long_video_id,
          seconds: topic.long_seconds,
          version: "long",
        });
      } else if (level === "somewhat" && topic.short_video_id) {
        items.push({
          id: topic.id,
          title: topic.title,
          videoId: topic.short_video_id,
          seconds: topic.short_seconds,
          version: "short",
        });
      }
    }
    return items;
  }, [opening, choiceTopics, selections]);

  const previewSeconds = preview.reduce((sum, item) => sum + (item.seconds ?? 0), 0);

  const iframeRefs = useRef<(HTMLIFrameElement | null)[]>([]);
  const playerRefs = useRef<(Player | null)[]>([]);

  const playAt = useCallback((index: number) => {
    playerRefs.current.forEach((player, playerIndex) => {
      if (!player) return;
      if (playerIndex === index) void player.play?.();
      else void player.pause?.();
    });
    setActiveIndex(index);
  }, []);

  const turnSoundOn = useCallback(() => {
    // Hide the button right away; the player may not confirm the change.
    setSoundConfirmed(true);
    const players = playerRefs.current.filter((player): player is Player => player !== null);

    // Unmute every already-loaded player during this click. Keeping those players
    // mounted lets the browser retain the permission when the playlist advances.
    players.forEach((player) => {
      void player.setVolume?.(100)?.catch?.(() => {});
      void player.unmute?.()?.catch?.(() => {});
    });
    const current = playerRefs.current[activeIndex];
    if (current) void current.play?.()?.catch?.(() => {});
  }, [activeIndex]);

  // Create each playlist player once so sound state survives video transitions.
  useEffect(() => {
    if (!playlist) return;
    let cancelled = false;
    setSoundConfirmed(false);
    playerRefs.current = iframeRefs.current.map((frame, index) => {
      if (!frame) return null;
      const player = new Player(frame);
      player.on("ready", () => {
        if (cancelled) return;
        player.on("ended", () => {
          if (cancelled || index + 1 >= playlist.length) return;
          void player.pause?.();
          const nextPlayer = playerRefs.current[index + 1];
          if (nextPlayer) void nextPlayer.play?.();
          setActiveIndex(index + 1);
        });
        if (index === 0) void player.play?.();
      });
      return player;
    });

    return () => {
      cancelled = true;
      playerRefs.current = [];
      iframeRefs.current = [];
    };
  }, [playlist]);

  if (topics === null) {
    return (
      <div className="aspect-video w-full animate-pulse rounded-2xl border border-primary/20 bg-lavender" />
    );
  }

  if (!opening?.long_video_id) {
    return (
      <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-2xl border border-primary/20 bg-lavender">
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#693D94] text-white shadow-brand">
            <Play className="h-6 w-6 translate-x-0.5" aria-hidden="true" />
          </span>
          <p
            style={{ fontFamily: "'Poppins', sans-serif" }}
            className="text-sm font-medium text-ink/70"
          >
            Instant demo coming soon
          </p>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------- playing */

  if (playlist && playlist.length > 0) {
    const totalSeconds = playlist.reduce((sum, item) => sum + (item.seconds ?? 0), 0);
    return (
      <div
        className="rounded-2xl border border-border/60 bg-background p-4 md:p-5"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="grid gap-5 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
          <div className="order-2 md:order-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Your demo</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {playlist.length} {playlist.length === 1 ? "video" : "videos"},{" "}
              {totalRuntimeLabel(totalSeconds)}
            </p>
            <ol className="mt-4 divide-y divide-border/60 border-y border-border/60">
              {playlist.map((item, index) => (
                <li key={`${item.id}-${index}`}>
                  <button
                    type="button"
                    onClick={() => playAt(index)}
                    className={`flex w-full items-start gap-3 py-3 text-left text-sm transition-colors ${
                      index === activeIndex ? "text-primary" : "text-ink hover:text-primary"
                    }`}
                  >
                    <span className="mt-0.5 text-xs text-muted-foreground">{index + 1}</span>
                    <span className="flex-1">
                      <span className={index === activeIndex ? "font-semibold" : "font-medium"}>
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {item.version === "short"
                          ? "Short version"
                          : item.version === "long"
                            ? "Full version"
                            : "Start here"}
                        {item.seconds ? ` · ${formatRuntime(item.seconds)}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => {
                setActiveIndex(0);
                setPlaylist(null);
              }}
              className="mt-4 text-sm font-semibold text-primary underline-offset-4 hover:underline"
            >
              Change my choices
            </button>
          </div>

          <div className="relative order-1 overflow-hidden rounded-xl bg-ink md:order-2">
            <div className="aspect-video w-full">
              {playlist.map((item, index) => (
                <iframe
                  key={`${item.videoId}-${index}`}
                  ref={(frame) => {
                    iframeRefs.current[index] = frame;
                  }}
                  src={gumletEmbedUrl(item.videoId)}
                  title={item.title}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  aria-hidden={index !== activeIndex}
                  className={index === activeIndex ? "h-full w-full border-0" : "hidden"}
                />
              ))}
            </div>
            {!soundConfirmed && (
              <Button
                type="button"
                onClick={turnSoundOn}
                className="absolute bottom-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-2 rounded-[6px] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-brand"
              >
                <Volume2 className="h-4 w-4" aria-hidden="true" />
                Turn sound on
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ----------------------------------------------------------- selecting */

  return (
    <div
      className="rounded-2xl border border-border/60 bg-background p-5 md:p-7"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">Instant demo</p>
      <h3 className="mt-2 text-xl font-semibold leading-tight text-ink md:text-2xl">
        Tell us what matters, watch only that.
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Tell us how much you care about each topic below, and we'll build a demo just for you.
        Important gets the full video, Somewhat gets a short one, and Not important is skipped.
      </p>
      <p className="mt-2 text-sm font-semibold text-ink">
        No email, no form. Press play and watch right away.
      </p>

      <div className="mt-6 rounded-xl border border-primary/20 bg-lavender p-4">
        <p className="text-sm font-semibold text-ink">
          <Check className="mr-1.5 inline h-4 w-4 text-primary" aria-hidden="true" />
          Always included: {opening.title}
          {opening.long_seconds ? ` · ${formatRuntime(opening.long_seconds)}` : ""}
        </p>
        {opening.blurb && (
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{opening.blurb}</p>
        )}
      </div>

      {choiceTopics.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">More topics are on the way.</p>
      ) : (
        <ul className="mt-5 divide-y divide-border/60 border-y border-border/60">
          {choiceTopics.map((topic) => {
            const singleLength =
              !topic.short_video_id ||
              !topic.short_seconds ||
              topic.short_seconds === topic.long_seconds;
            const visibleChoices = singleLength
              ? choices.filter((c) => c.value !== "somewhat")
              : choices;
            const runtimeFor = (value: Importance) => {
              if (singleLength) return null;
              if (value === "important" && topic.long_seconds)
                return `+ ${formatRuntime(topic.long_seconds)}`;
              if (value === "somewhat" && topic.short_seconds)
                return `+ ${formatRuntime(topic.short_seconds)}`;
              return null;
            };
            return (
              <li
                key={topic.id}
                className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between"
              >
                <div className="md:pr-6">
                  <p className="text-sm font-semibold text-ink">
                    {topic.title}
                    {singleLength && topic.long_seconds ? (
                      <span className="ml-2 font-normal text-muted-foreground">
                        {formatRuntime(topic.long_seconds)}
                      </span>
                    ) : null}
                  </p>
                  {topic.blurb && (
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {topic.blurb}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-start gap-2">
                  {visibleChoices.map((choice) => {
                    const active = selections[topic.id] === choice.value;
                    const runtime = runtimeFor(choice.value);
                    return (
                      <div key={choice.value} className="flex flex-col items-center">
                        <button
                          type="button"
                          aria-pressed={active}
                          onClick={() =>
                            setSelections((prev) => ({ ...prev, [topic.id]: choice.value }))
                          }
                          className={`rounded-[6px] border px-3 py-2 text-xs font-semibold transition-colors ${
                            active
                              ? "border-[#693D94] bg-[#693D94] text-primary-foreground"
                              : "border-primary/30 text-primary hover:bg-lavender"
                          }`}
                        >
                          {choice.label}
                        </button>
                        {runtime && (
                          <span className="mt-1 text-xs text-muted-foreground">{runtime}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={() => {
            setSoundConfirmed(false);
            setActiveIndex(0);
            setPlaylist(preview);
          }}
          className="inline-flex items-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5"
        >
          Watch my demo
        </button>
        <p className="text-sm text-muted-foreground">
          {preview.length} {preview.length === 1 ? "video" : "videos"}
          {previewSeconds > 0 ? `, ${totalRuntimeLabel(previewSeconds)}` : ""}
          {choiceTopics.length > 0 ? ` · ${answeredCount} of ${choiceTopics.length} marked` : ""}
        </p>
      </div>
    </div>
  );
}
